import React from "react";
import { Typography, List, Space, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { formatEuro } from "../PricingCalculatorHelpers";
import { CalculatedPrices } from "../PricingCalculatorTypes";
import { DELIVERY, AIR_FREIGHT } from "../../../lib/pricingDefaults";

const { Text } = Typography;

interface DeliveryCostsSectionProps {
  calculatedPrices: CalculatedPrices;
}

export const DeliveryCostsSection: React.FC<DeliveryCostsSectionProps> = ({
  calculatedPrices,
}) => {
  const { state } = useBookingWizard();
  const { t } = useTranslation();

  if (state.currentStep < DELIVERY.DISPLAY_FROM_STEP) {
    return null;
  }

  const totalCartons = (state.products || []).reduce(
    (sum, product) => sum + (product.cartons || 0),
    0
  );
  const estimatedPallets = totalCartons > 0 ? Math.ceil(totalCartons / 8) : 0;

  return (
    <div style={{ marginBottom: 16 }}>
      <Text strong>
        {t(
          "bookingWizard.pricingCalculator.deliveryCostsSectionTitle",
          "Delivery"
        )}
      </Text>
      <List size="small" itemLayout="horizontal" style={{ marginTop: 8 }}>
        {/* Diesel surcharge */}
        {state.currentStep >= AIR_FREIGHT.DISPLAY_FROM_STEP &&
          calculatedPrices.dieselSurcharge > 0 && (
            <List.Item
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Space>
                <Text>
                  {t(
                    "bookingWizard.pricingCalculator.dieselSurcharge",
                    "Diesel surcharge"
                  )}
                </Text>
                <Tooltip
                  title={t(
                    "bookingWizard.pricingCalculator.dieselSurchargeTooltip",
                    "Additional fee to account for fuel costs"
                  )}
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
              <Text>{formatEuro(calculatedPrices.dieselSurcharge)}</Text>
            </List.Item>
          )}

        {/* Delivery cost */}
        <List.Item style={{ display: "flex", justifyContent: "space-between" }}>
          <Space>
            <Text>
              {t(
                "bookingWizard.pricingCalculator.deliveryCost",
                "Delivery cost"
              )}
            </Text>
            <Tooltip
              title={t(
                "bookingWizard.pricingCalculator.deliveryCostTooltipDetailed",
                "Base fee €{{minFee}} + €{{perCarton}} per carton (currently {{cartons}} cartons ≈ {{pallets}} pallets), capped at €{{maxFee}}.",
                {
                  minFee: DELIVERY.MIN_FEE.toFixed(2),
                  perCarton: DELIVERY.PER_CARTON_FEE.toFixed(2),
                  maxFee: DELIVERY.MAX_FEE.toFixed(2),
                  cartons: totalCartons,
                  pallets: estimatedPallets,
                }
              )}
            >
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
          <Text>{formatEuro(calculatedPrices.deliveryCost)}</Text>
        </List.Item>
      </List>
    </div>
  );
};





